import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Grid,
    IconButton,
    Button,
    CircularProgress,
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import {Clear, Edit, ExpandMore} from '@material-ui/icons';
import {PBold} from 'components/TypographyShortcuts';
import React, {useContext, useEffect, useRef, useState} from 'react';
import fields from './fields';
import {AutoField} from 'components/forms/fields';
import {FormProvider, useFormContext} from 'react-hook-form';
import {PrintFieldValue} from 'components';
import {useNonPersistentFormMethods} from 'components/forms/hooks';
import {usePutProfile} from 'hooks';
import {LocalizationProvider} from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import {AppCtx} from 'context/AppContext';
import {getInitialValues} from '../AutoSection';
import {getSchema} from '../AutoSection';
import {UserContext} from 'context/UserContext';
import Show from 'components/utils/Show';

const useStyles = makeStyles(theme => ({
    root: {
        width: '80%',
        [theme.breakpoints.down('xs')]: {
            width: '100%',
        },
        [theme.breakpoints.only('sm')]: {
            width: '90%',
        },
    },
    alignLeft: {
        float: 'left',
    },
    alignRight: {
        float: 'right',
    },
    field: {
        fontWeight: 'bold',
        color: theme.palette.grey['600'],
    },
    valueDisplayGrid: {
        columnGap: '1em',
    },
    separateBottom: {
        borderBottom: `1px solid ${theme.palette.grey[200]}`,

        '&:last-of-type': {
            borderBottom: 'none',
        },
    }
}));

const getSections = () => {
    const sections = {};
    Object.keys(fields).forEach(field => {
        if (fields[field].section in sections) {
            sections[fields[field].section].push(field);
        } else {
            sections[fields[field].section] = [field];
        }
    });
    return sections;
};

const orderOfSections = [
    'Name & ID',
    'Citizenship',
    'Birth',
    'Hair & Eye Color',
    'Home Address',
    'Delivery Address',
    'Phone & Email',
    'Preferred Contact Method',
    'Emergency Contact',
    'Past Convictions & Drug Tests',
    'National Driver Registry Consent',
];

const FormField = ({field, value, profile, resetUI, readOnly}) => {
    const classes = useStyles();
    const [editField, setEditField] = useState(false);
    const {reset, getValues} = useFormContext();

    useEffect(() => {
        if (resetUI) {
            setEditField(false);
        }
    }, [resetUI]);

    const updateEditField = () => {
        if (editField) {
            // the edit field was active. We want to reset this field to its original value
            // and also check - if there are no more differences between original profile
            // object and current form state, then we reset dirtyFields to tell submit button
            // to deactivate
            let formState = {...profile, ...getValues()};
            formState[field] = value;
            let dirty =
                JSON.stringify(formState) === JSON.stringify(profile) ? false : true;
            reset(formState, {dirtyFields: dirty});
        }
        setEditField(!editField);
    };

    return (
        <Grid item className={classes.separateBottom}>
            <Grid container alignItems='center'>
                <Grid
                    container
                    item
                    xs={readOnly ? 12 : 11}
                    justifyContent={'space-between'}
                    className={classes.valueDisplayGrid}
                >
                    {!editField && [
                        <Grid item xs className={classes.field} key='label'>
                            {fields[field].label}
                        </Grid>,
                        <Grid item key='value'>
                            <PrintFieldValue fieldName={field} value={value} />
                        </Grid>,
                    ]}
                    {editField && (
                        <AutoField
                            field={fields[field]}
                            name={`${field}`}
                            variant='outlined'
                            size='small'
                        />
                    )}
                </Grid>
                <Show condition={!readOnly}>
                    <Grid item xs={1}>
                        <Box textAlign='center'>
                            <IconButton
                                aria-label={`edit-${field}`}
                                color='default'
                                onClick={updateEditField}
                            >
                                {!editField && <Edit />}
                                {editField && <Clear />}
                            </IconButton>
                        </Box>
                    </Grid>
                </Show>
            </Grid>
        </Grid>
    );
};

const AccordionSection = ({section, profile, resetUI, expanded, readOnly}) => {
    const sections = getSections();
    const domRef = useRef(null);

    return (
        <Grid item>
            <Accordion
                ref={domRef}
                expanded={expanded}
            >
                <AccordionSummary
                    expandIcon={<ExpandMore />}
                    aria-controls={`panel-${section}-content`}
                    id={`panel-${section}-header`}
                >
                    <PBold>{section}</PBold>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container direction='column'>
                        {sections[section]?.map(field => {
                            return (
                                <FormField
                                    field={field}
                                    value={profile?.[field]}
                                    profile={profile}
                                    key={`field-${field}`}
                                    resetUI={resetUI}
                                    readOnly={readOnly}
                                />
                            );
                        })}
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </Grid>
    );
};

const ProfileForm = ({profile, setProfile, user, readOnly}) => {
    // const classes = useStyles();
    const putProfile = usePutProfile();
    const [resetUI, setResetUI] = useState(false);
    const {setSnackOpen, setSnackProps} = useContext(AppCtx);
    const {updateUser, actions} = useContext(UserContext);

    const methods = useNonPersistentFormMethods({
        initialValues: getInitialValues(profile, Object.keys(fields), fields),
        schema: getSchema(Object.keys(fields), fields),
    });

    const onSubmit = e => {
        e.preventDefault();
        if (Object.keys(errors).length === 0) {
            putProfile.put(methods.getValues());
        }
    };

    useEffect(() => {
        if (resetUI) {
            setResetUI(false);
        }
    }, [resetUI]);

    useEffect(() => {
        if (putProfile?.result?.status === 'ok') {
            const result = putProfile?.result?.user_profile;
            setSnackProps({
                severity: 'success',
                message: `Profile updates saved!`,
            });
            setSnackOpen(true);
            // mmmmm, snackbar is open!
            setProfile(result);
            updateUser({
                firstname: result.applicant_name_first,
                lastname: result.applicant_name_last,
            });
            actions.updateProfile({
                applicant_MMC: result.applicant_MMC,
                applicant_name_middle: result.applicant_name_middle,
            });
            methods.reset(result, {
                dirtyFields: false,
            });
            setResetUI(true);
        } else if (
            putProfile?.result?.status === 'error' ||
            putProfile?.error?.errors?.length
        ) {
            setSnackProps({
                severity: 'error',
                message: `Something went wrong. Errors: ${JSON.stringify(
                    putProfile?.error
                )}`,
            });
            setSnackOpen(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [putProfile?.result, putProfile?.error]);

    const isDirty = !!Object.keys(methods.formState.dirtyFields).length;
    const {errors} = methods;

    return (
        <Box>
            <FormProvider {...methods}>
                <LocalizationProvider dateAdapter={MomentUtils}>
                    <form onSubmit={onSubmit}>
                        <Grid container spacing={2} direction='column'>
                            {orderOfSections.map(section => {
                                return (
                                    <AccordionSection
                                        section={section}
                                        profile={profile}
                                        key={section}
                                        resetUI={resetUI}
                                        expanded={readOnly}
                                        readOnly={readOnly}
                                    />
                                );
                            })}
                            <Show condition={!readOnly}>
                                <Grid item key='submit'>
                                    <Button
                                        variant='contained'
                                        color={isDirty ? 'primary' : 'default'}
                                        disabled={
                                            isDirty && !Object.keys(errors).length
                                                ? false
                                                : true
                                        }
                                        fullWidth
                                        type='submit'
                                    >
                                        Save Changes
                                    </Button>
                                </Grid>
                                {putProfile?.submissionInProgress && (
                                    <Grid
                                        container
                                        justifyContent='center'
                                        alignItems='center'
                                    >
                                        <CircularProgress />
                                    </Grid>
                                )}
                            </Show>
                        </Grid>
                    </form>
                </LocalizationProvider>
            </FormProvider>
        </Box>
    );
};

export default ProfileForm;
